import type { SqliteExecutor } from '@/data/sqliteExecutor.ts'
import {
  applyDeltaBatch,
  setCursor,
  setEntityVersion,
  setSubscribedTypes,
  type DeltaPage,
} from './catalogReplica.ts'
import {
  BLOB_FORMAT_VERSION,
  ENTITY_VERSIONS,
  SHARED_BLOB_KEY,
  parsePages,
  pageRowCount,
  type BlobManifest,
  type BlobManifestEntry,
} from './catalogBlob.ts'

// Where the prebuilt snapshot blobs live. A deploy step uploads the scraper's
// output (manifest.json + *.ndjson.gz) here; until that's wired the var is unset
// and every bootstrap attempt cleanly falls back to the from-0 feed drain.
const BLOB_BASE_URL = import.meta.env.VITE_CATALOG_BLOB_BASE_URL as string | undefined

export type BootstrapOutcome = 'applied' | 'skipped'

export type SyncPhase = 'downloading' | 'extracting' | 'applying' | 'migrating' | 'updating' | 'images'
export type ProgressReport = (report: { phase: SyncPhase; done: number; total: number }) => void

function manifestUrl(region: string): string {
  return `${BLOB_BASE_URL}/${region}/manifest.json`
}

async function fetchManifest(region: string): Promise<BlobManifest | null> {
  if (!BLOB_BASE_URL) {
    console.warn('Offline bootstrap: VITE_CATALOG_BLOB_BASE_URL unset')
    return null
  }
  try {
    const response = await fetch(manifestUrl(region))
    if (!response.ok) {
      console.warn(`Offline bootstrap: manifest ${manifestUrl(region)} -> HTTP ${response.status}`)
      return null
    }
    const manifest = (await response.json()) as BlobManifest
    if (manifest.formatVersion !== BLOB_FORMAT_VERSION) {
      console.warn(
        `Offline bootstrap: manifest formatVersion ${manifest.formatVersion} != ${BLOB_FORMAT_VERSION}`,
      )
      return null
    }
    return manifest
  } catch (error) {
    console.warn('Offline bootstrap: manifest fetch failed', error)
    return null
  }
}

// A blob is usable only if every entity it was built against still matches this
// client's projection version. Any drift ⇒ the whole snapshot is rejected and the
// caller drains the live feed instead, so blobs are never the only correctness path.
function isVersionCompatible(manifest: BlobManifest): boolean {
  return Object.entries(ENTITY_VERSIONS).every(
    ([entity, version]) => manifest.entityVersions[entity] === version,
  )
}

async function toHex(buffer: ArrayBuffer): Promise<string> {
  return [...new Uint8Array(buffer)].map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function fetchBlobBytes(region: string, entry: BlobManifestEntry): Promise<ArrayBuffer> {
  const response = await fetch(`${BLOB_BASE_URL}/${region}/${entry.file}`)
  if (!response.ok) throw new Error(`Blob ${entry.key} fetch failed: ${response.status}`)
  const gzipped = await response.arrayBuffer()
  if (entry.sha256 && globalThis.crypto?.subtle) {
    const digest = await toHex(await crypto.subtle.digest('SHA-256', gzipped))
    if (digest !== entry.sha256) throw new Error(`Blob ${entry.key} sha256 mismatch`)
  }
  return gzipped
}

async function decodeBlob(entry: BlobManifestEntry, gzipped: ArrayBuffer): Promise<string> {
  const stream = new Response(gzipped).body?.pipeThrough(new DecompressionStream('gzip'))
  if (!stream) throw new Error(`Blob ${entry.key} could not be decompressed`)
  return new Response(stream).text()
}

// Two monotonic passes so the UI phase doesn't ping-pong per blob: download +
// verify every blob first ('downloading'), then decode + apply them ('applying').
// The gzipped buffers held between passes are small (the whole set is a few MB);
// decode + parse stays one blob at a time so the heap never holds the full
// uncompressed catalog. One transaction per blob (applyDeltaBatch) = one web flush.
// Apply is chunked by ROW count (not per blob): the shared/manga blob is a single
// huge slice, so a per-blob bar sticks on it. Rows give a smooth "saved N of M".
const APPLY_CHUNK_ROWS = 2000
const yieldMacrotask = () => new Promise<void>((resolve) => setTimeout(resolve))

async function downloadApplyEntries(
  executor: SqliteExecutor,
  region: string,
  entries: BlobManifestEntry[],
  report?: ProgressReport,
  signal?: AbortSignal,
): Promise<void> {
  const blobTotal = entries.length

  const buffers: { entry: BlobManifestEntry; gzipped: ArrayBuffer }[] = []
  for (const entry of entries) {
    if (signal?.aborted) throw new Error('Bootstrap aborted')
    report?.({ phase: 'downloading', done: buffers.length, total: blobTotal })
    buffers.push({ entry, gzipped: await fetchBlobBytes(region, entry) })
  }
  report?.({ phase: 'downloading', done: blobTotal, total: blobTotal })

  // Count pass: decode each blob (from the held gzipped buffer) to total the rows
  // so apply can report a determinate row-based bar. Decode is cheap vs the SQLite
  // write, and only one blob's text is held at a time (heap stays bounded).
  let totalRows = 0
  for (const { entry, gzipped } of buffers) {
    report?.({ phase: 'extracting', done: 0, total: 0 })
    const text = await decodeBlob(entry, gzipped)
    for (const page of parsePages(text)) totalRows += pageRowCount(page)
  }

  // Apply pass: stream each blob's pages into row-sized chunks, one transaction +
  // one progress tick per chunk, yielding between so the bar paints.
  let rowsDone = 0
  let batch: DeltaPage[] = []
  let batchRows = 0
  const flush = async () => {
    if (batch.length === 0) return
    await yieldMacrotask()
    await applyDeltaBatch(executor, batch)
    rowsDone += batchRows
    report?.({ phase: 'applying', done: rowsDone, total: totalRows })
    batch = []
    batchRows = 0
  }

  report?.({ phase: 'applying', done: 0, total: totalRows })
  for (const { entry, gzipped } of buffers) {
    const text = await decodeBlob(entry, gzipped)
    for (const page of parsePages(text)) {
      // Between chunks the last applyDeltaBatch transaction has committed, so
      // bailing here on abort (logout teardown) leaves no open transaction to
      // strand the DB's write lock across the imminent reload.
      if (signal?.aborted) throw new Error('Bootstrap aborted')
      batch.push(page as DeltaPage)
      batchRows += pageRowCount(page)
      if (batchRows >= APPLY_CHUNK_ROWS) await flush()
    }
  }
  await flush()
}

function entriesForKeys(manifest: BlobManifest, keys: string[]): BlobManifestEntry[] | null {
  const blobByKey = new Map(manifest.blobs.map((blob) => [blob.key, blob]))
  const needed = keys.map((key) => blobByKey.get(key))
  return needed.some((entry) => entry === undefined) ? null : (needed as BlobManifestEntry[])
}

// Seed the replica from prebuilt blobs instead of draining catalogChanges from
// seq 0. Returns 'skipped' when blobs are unconfigured, unreachable,
// version-incompatible, or missing a needed slice — on a fresh install the caller
// treats this as fatal (no full-feed fallback) and retries later. Cursor is
// advanced only after every needed blob applies, so a crash mid-way just re-runs
// the (idempotent) bootstrap rather than stranding a half cursor.
export async function bootstrapReplicaFromBlobs(opts: {
  executor: SqliteExecutor
  types: string[]
  region: string
  report?: ProgressReport
  signal?: AbortSignal
}): Promise<BootstrapOutcome> {
  const manifest = await fetchManifest(opts.region)
  if (!manifest) return 'skipped'
  if (!isVersionCompatible(manifest)) {
    console.warn('Offline bootstrap: manifest entity versions incompatible with this client')
    return 'skipped'
  }

  const needed = entriesForKeys(manifest, [SHARED_BLOB_KEY, ...opts.types])
  if (!needed) {
    console.warn('Offline bootstrap: manifest missing a required blob slice')
    return 'skipped'
  }

  try {
    await downloadApplyEntries(opts.executor, opts.region, needed, opts.report, opts.signal)
  } catch (error) {
    console.warn('Offline bootstrap: blob download/apply failed', error)
    return 'skipped'
  }

  await setCursor(opts.executor, manifest.builtAtSeq)
  for (const [entity, version] of Object.entries(ENTITY_VERSIONS)) {
    await setEntityVersion(opts.executor, entity, version)
  }
  await setSubscribedTypes(opts.executor, opts.types)
  return 'applied'
}

// Widen path: pull just the added types' blobs (shared already present) instead
// of a from-0 feed backfill. Like the feed backfill it leaves the global cursor
// alone — the slice's historical rows sit at seq <= cursor and forward sync keeps
// it current. 'skipped' ⇒ caller runs the feed backfill.
export async function applyTypeBlobs(opts: {
  executor: SqliteExecutor
  types: string[]
  region: string
  report?: ProgressReport
  signal?: AbortSignal
}): Promise<BootstrapOutcome> {
  if (opts.types.length === 0) return 'applied'
  const manifest = await fetchManifest(opts.region)
  if (!manifest || !isVersionCompatible(manifest)) return 'skipped'
  const needed = entriesForKeys(manifest, opts.types)
  if (!needed) return 'skipped'
  try {
    await downloadApplyEntries(opts.executor, opts.region, needed, opts.report, opts.signal)
  } catch {
    return 'skipped'
  }
  return 'applied'
}
